import { ChangeDetectionStrategy, Component, OnInit } from "@angular/core";
import { BridgeComponent } from "../bridge.component";
import { FE_SlackChannel, FE_SlackLink, FE_SlackTeam } from "@shared/models/slack";
import { SlackApiService } from "@shared/services/integrations/slack-api.service";
import { ScalarClientApiService } from "@shared/services/scalar/scalar-client-api.service";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ConfigScreenBridgeComponent } from "../config-screen/config-screen.bridge.component";
import { LogService } from "@shared/services/log.service";
import { trustResourceUrl } from "@shared/helpers/SafeUrlHelper";

import { SpinnerComponent } from "../../../elements/spinner/spinner.component";
import { FieldComponent } from "../../../elements/field/field.component";

interface SlackConfig {
    botUserId: string;
    link: FE_SlackLink;
}

@Component({
    selector: "my-slack-bridge-config",
    templateUrl: "slack.bridge.component.html",
    styleUrls: ["slack.bridge.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ConfigScreenBridgeComponent, SpinnerComponent, FieldComponent, TranslatePipe]
})
export class SlackBridgeConfigComponent extends BridgeComponent<SlackConfig> implements OnInit {

    public teamId: string;
    public channelId: string;
    public teams: FE_SlackTeam[];
    public channels: FE_SlackChannel[];
    public isBusy: boolean;
    public loadingTeams = true;
    public needsAuth = false;
    public authUrl: SafeUrl;

    private timerId: any;

    constructor(
        private readonly slack: SlackApiService,
        private readonly scalar: ScalarClientApiService,
        private readonly sanitizer: DomSanitizer,
        public translate: TranslateService,
        logService: LogService,
    ) {
        super("slack", translate, logService);
        this.translate = translate;
    }

    public get teamOptions(): { key: string, value: string }[] {
        return this.teams.map(t => ({ key: t.id, value: t.name }));
    }

    public get channelOptions(): { key: string, value: string }[] {
        return this.channels.map(t => ({ key: t.id, value: t.name }));
    }

    public ngOnInit() {
        super.ngOnInit();

        this.tryLoadTeams();
    }

    private tryLoadTeams() {
        this.slack.getTeams().then(teams => {
            this.teams = teams;
            this.teamId = this.teams[0].id;
            this.needsAuth = false;
            this.loadingTeams = false;
            this.loadChannels();

            if (this.timerId) {
                clearInterval(this.timerId);
            }
        }).catch(error => {
            if (error.status === 404) {
                this.needsAuth = true;

                if (!this.authUrl) {
                    this.slack.getAuthUrl().then(url => {
                        this.authUrl = trustResourceUrl(this.sanitizer, url);
                        this.loadingTeams = false;
                    }).catch(error2 => {
                        this.logService.error(error2);
                        this.translate.get('Error getting Slack authorization information').subscribe((res: string) => {
                            this.toaster.pop("error", res);
                        });
                    });

                    this.timerId = setInterval(() => {
                        this.tryLoadTeams();
                    }, 1000);
                }
            } else {
                this.logService.error(error);
                this.translate.get('Error getting teams').subscribe((res: string) => {
                    this.toaster.pop("error", res);
                });
            }
        });
    }

    public get isBridged(): boolean {
        return !!this.bridge.config.link;
    }

    public loadChannels() {
        this.isBusy = true;
        this.slack.getChannels(this.teamId).then(channels => {
            this.channels = channels;
            this.channelId = this.channels[0].id;
            this.isBusy = false;
        }).catch(error => {
            this.logService.error(error);
            this.translate.get('Error getting channels for team').subscribe((res: string) => {
                this.toaster.pop("error", res);
            });
            this.isBusy = false;
        });
    }

    public async bridgeRoom(): Promise<any> {
        this.isBusy = true;

        try {
            await this.scalar.inviteUser(this.roomId, this.bridge.config.botUserId);
        } catch (e) {
            if (!e.response?.error?._error?.message?.includes("already in the room")) {
                this.isBusy = false;
                this.translate.get('Error inviting bridge').subscribe((res: string) => {
                    this.toaster.pop("error", res);
                });
                return;
            }
        }

        this.slack.bridgeRoom(this.roomId, this.teamId, this.channelId).then(link => {
            this.bridge.config.link = link;
            this.isBusy = false;
            this.translate.get('Bridge requested').subscribe((res: string) => {
                this.toaster.pop("success", res);
            });
        }).catch(error => {
            this.isBusy = false;
            this.logService.error(error);
            this.translate.get('Error requesting bridge').subscribe((res: string) => {
                this.toaster.pop("error", res);
            });
        });
    }

    public unbridgeRoom(): void {
        this.isBusy = true;
        this.slack.unbridgeRoom(this.roomId).then(() => {
            this.bridge.config.link = null;
            this.isBusy = false;
            this.translate.get('Bridge removed').subscribe((res: string) => {
                this.toaster.pop("success", res);
            });
        }).catch(error => {
            this.isBusy = false;
            this.logService.error(error);
            this.translate.get('Error removing bridge').subscribe((res: string) => {
                this.toaster.pop("error", res);
            });
        });
    }
}
